import { component } from 'picoapp';

interface ShippingInfoConfig {
  linkClass: string
  url: string
}

export default component(async (node) => {
  const shippingStrings = theme.strings.product.shipping;

  const config: ShippingInfoConfig = JSON.parse(node.dataset.config);
  const { linkClass, url } = config;

  const initShippingTime = async () => {
    const { default: Holidays } = (await import(
      /* webpackChunkName: "data-holidays" */
      'modules/date-holidays'
    ));

    const dateHolidays = new Holidays();
    dateHolidays.init(Shopify.country);

    const holidays = dateHolidays.getHolidays();

    const getNextWorkDayFromDate = (date: Date) => {
      const finalDate = new Date(date.getTime());
      let hasResolved = false;

      while (!hasResolved) {
        const matchingHoliday = (finalDate.getDay() === 6 || finalDate.getDay() === 0)
          || holidays.find((holiday) => {
            const { start, end } = holiday;
            return holiday.type === 'public' && finalDate >= start && finalDate <= end;
          }) !== undefined;

        if (matchingHoliday) {
          finalDate.setDate(finalDate.getDate() + 1);
        } else {
          hasResolved = true;
        }
      }

      return finalDate;
    };

    const MILISECONDS_IN_A_SECOND = 1000;
    const SECONDS_IN_A_MINUTE = 60;
    const MINUTES_IN_AN_HOUR = 60;
    const SECONDS_IN_AN_HOUR = 3600;

    const currentDate = new Date();

    const currentDateDailyShippingTimeKey = currentDate.getDay() === 5
      ? 'dailyShippingTimeFriday'
      : 'dailyShippingTimeDefault';

    let orderDate = new Date(currentDate.getTime());

    // if current time is after daily shipping time, set base date to next day
    if (orderDate.getHours() >= theme.config.shipping[currentDateDailyShippingTimeKey]) {
      orderDate.setDate(orderDate.getDate() + 1);
    }

    // set order date to next work day
    orderDate = getNextWorkDayFromDate(orderDate);

    const { shippingTimeMin, shippingTimeMax } = theme.config.shipping;

    // set min delivery date
    let deliveryDateMin = new Date(orderDate.getTime());
    deliveryDateMin.setDate(deliveryDateMin.getDate() + shippingTimeMin);
    deliveryDateMin = getNextWorkDayFromDate(deliveryDateMin);

    // set max delivery date
    let deliveryDateMax = new Date(deliveryDateMin.getTime());
    deliveryDateMax.setDate(deliveryDateMax.getDate() + (shippingTimeMax - shippingTimeMin));
    deliveryDateMax = getNextWorkDayFromDate(deliveryDateMax);

    // calculate hours, minutes, seconds from current time up to daily shipping time for order date
    const orderDateDailyShippingTimeKey = orderDate.getDay() === 5
      ? 'dailyShippingTimeFriday'
      : 'dailyShippingTimeDefault';

    orderDate.setHours(theme.config.shipping[orderDateDailyShippingTimeKey]);
    orderDate.setMinutes(0);
    orderDate.setSeconds(0);

    // write info
    const interval = setInterval(() => {
      const currentTime = new Date().getTime();
      const orderDateTime = orderDate.getTime();

      const miliseconds = orderDateTime - currentTime;

      if (miliseconds <= 0) {
        clearInterval(interval);
        initShippingTime();
        return;
      }

      const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      };

      const dateMinTranslation = deliveryDateMin.toLocaleDateString(Shopify.locale, dateOptions);
      const dateMaxTranslation = deliveryDateMax.toLocaleDateString(Shopify.locale, dateOptions);
      const expectedDateString = `${dateMinTranslation} - ${dateMaxTranslation}`;

      const totalSeconds = miliseconds / MILISECONDS_IN_A_SECOND;
      const hours = Math.floor(totalSeconds / SECONDS_IN_AN_HOUR).toString();

      const minutes = Math.floor((totalSeconds % SECONDS_IN_AN_HOUR) / MINUTES_IN_AN_HOUR)
        .toString();

      const seconds = Math.floor(totalSeconds % SECONDS_IN_A_MINUTE).toString();

      /* eslint-disable @typescript-eslint/indent */
      node.innerHTML = `<p>${shippingStrings.expectedDelivery.replace('%expectedDate%', expectedDateString)}</p>
        <p class="product__order-before">
          ${shippingStrings.orderBefore
            .replace('%hours%', hours)
            .replace('%minutes%', minutes)
            .replace('%seconds%', seconds)}
        </p>
        ${url ? `<a href="${url}" class="${linkClass}"></a>` : ''}`;
      /* eslint-enable @typescript-eslint/indent */
    }, 1000);
  };

  const observe = () => {
    const observer = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        initShippingTime();
        observer.disconnect();
      }
    });

    observer.observe(node);
  };

  const init = () => {
    observe();
  };

  init();
});
